import { ReactNode } from 'react'
import { TableColumnDefinition } from 'components/molecules/tables/Table'
import * as isNumber from 'lodash/isNumber'
import { Editable, EditingRow, FormRow, Paginatable, PaginatableMeta, BasePaginationState } from 'interfaces/table'
import TableNoWrapValue from 'components/atoms/tables/TableNoWrapValue'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { NOT_APPLICABLE } from 'constants/accounts'
import AbsentValue from 'components/atoms/AbsentValue'
import { PaginatorStateProps } from 'components/molecules/tables/Paginator'
import { PageSizeSelectorStateProps } from 'components/atoms/tables/PageSizeSelector'
import { PAGE_SIZE_OPTIONS } from 'constants/component'
import { CurrencyRoundFormat } from 'interfaces/format'

const EDITING_ROW: EditingRow = 'EDITING_ROW'

export function isFormRow<TProps extends {}> (row: Editable<TProps>): row is FormRow<TProps> {
  return (row as any).__type === EDITING_ROW
}

export function isEmptyFormRow<TProps extends {}> (row: Editable<TProps>): row is FormRow<TProps> {
  return isFormRow(row) && row.__props === null
}

export function createFormRow<TProps extends {}> (props: TProps | null) {
  const formRow: FormRow<TProps> = {
    __type: EDITING_ROW,
    __props: props,
  }
  return formRow
}

export function getRowProps<TProps extends {}> (row: Editable<TProps>): TProps | null {
  return isFormRow(row) ? row.__props : row
}

export function makeColumnsEditable<TProps> (columns: TableColumnDefinition<TProps, any>[], formComponent: () => ReactNode): TableColumnDefinition<Editable<TProps>, Editable<TProps>>[] {
  type TEditable = Editable<TProps>

  const nextFirstColumn = ((): TableColumnDefinition<TEditable, TEditable> => {
    const firstColumn = columns[0]
    const { getColSpan } = firstColumn
    const identity = (value: TProps) => value
    const component = firstColumn.component || identity
    const propsSelector = firstColumn.propsSelector || identity

    return {
      ...firstColumn,
      propsSelector: identity,
      component: (t) => isFormRow(t)
        ? formComponent()
        : component(propsSelector(t)),
      getColSpan: (t) => {
        if (isFormRow(t)) {
          return columns.length
        }

        if (!getColSpan) {
          return 1
        }

        return getColSpan(propsSelector(t))
      },
    }
  })()

  const restColumns = columns.slice(1).map((column): TableColumnDefinition<TEditable, TEditable> => {
    const { getColSpan } = column
    const identity = (value: TProps) => value
    const component = column.component || identity
    const propsSelector = column.propsSelector || identity

    return {
      ...column,
      propsSelector: identity,
      component: (t) => isFormRow(t)
        ? null
        : component(propsSelector(t)),
      getColSpan: (t) => {
        if (isFormRow(t)) {
          return 0
        }

        if (!getColSpan) {
          return 1
        }

        return getColSpan(propsSelector(t))
      },
    }
  })

  return [nextFirstColumn, ...restColumns]
}

export const formatTableCurrency = (amount: number | null | undefined, format?: CurrencyRoundFormat) =>
  isNumber(amount)
    ? <TableNoWrapValue>{ formatUSCurrency(amount, format) }</TableNoWrapValue>
    : <AbsentValue children={ NOT_APPLICABLE } />

export const getPaginatorProps = <TApp extends PaginatableMeta, TData extends Paginatable<any, any>>(app: TApp, data: TData): PaginatorStateProps => {
  const { page, pageSize } = app
  const { count } = data

  return {
    page,
    pageSize,
    recordsCount: count,
  }
}

export const getPageSizeProps = <TApp extends PaginatableMeta>(app: TApp): PageSizeSelectorStateProps => {
  const { pageSize } = app

  return {
    current: pageSize,
    options: PAGE_SIZE_OPTIONS,
  }
}

export const isBasePaginationPristine = (state: BasePaginationState<any>): boolean => {
  return !state.isLoading
    && state.errorCode === null
    && state.pagination.results.length === 0
    && !state.pagination.pagination
}
